import { ITab } from './DspAdGroupDictsTab';
import AccessGuard from '../../components/AccessGuard';
import { USER_GROUP_ADMIN } from '../../types/User';
import TabPanel from '../../components/TabPanel';
import Column from '../../components/Column';
import Row from '../../components/Row';
import { AlertColor, Box, Button, Modal, Switch, TextField, Tooltip, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CDGrid, { CustomPagination } from '../../components/CDGrid';
import ApiService from '../../ApiService';
import React, { ChangeEvent, useEffect, useState } from 'react';
import {
    GridColDef,
    GridFooterContainer,
    GridPaginationModel,
    GridRenderEditCellParams,
    GridSortModel,
} from '@mui/x-data-grid';
import theme from '../../theme';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { AudienceMapping, AudienceMappingOptions } from '../../types/DspEntity';
import { AxiosPageableResponse } from '../../types/AxiosPageableResponse';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { InfoMessage, InfoMessageProps } from '../../components/InfoMessage';
import { AxiosResponse } from 'axios';

function AudienceMappingFooterGrid(props: { totalElements: number; numberOfElements: number }) {
    return (
        <GridFooterContainer>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginRight: '10px' }}>
                <CustomPagination />
                <span>
                    Showing {props.numberOfElements} of {props.totalElements} records
                </span>
            </div>
        </GridFooterContainer>
    );
}

const AudienceMappingTab = ({ tab, activeTab }: { tab: ITab; activeTab: number }) => {
    const defaultPageSize: number = 50;
    const pageValueArray = [10, 20, 50, 75, 100];
    const [list, setList] = useState<AudienceMapping[] | null>(null);
    const [open, setOpen] = useState(false);
    const [page, setPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [pageSize, setPageSize] = React.useState(defaultPageSize);
    const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'id', sort: 'desc' }]);
    const [infoMessage, setInfoMessage] = useState<InfoMessageProps>({
        message: null,
    });
    const [formData, setFormData] = useState<{
        strategyTypeId: number | '';
        keyValue: string;
        aacsc: string;
        amazonAudienceName: string;
        category: string;
        subCategory: string;
        source: string;
        audienceFeeType: string;
        name: string;
        options: AudienceMappingOptions | null;
    }>({
        strategyTypeId: '',
        keyValue: '',
        aacsc: '',
        amazonAudienceName: '',
        category: '',
        subCategory: '',
        source: '',
        audienceFeeType: '',
        name: '',
        options: null, // To store all dropdown options
    });

    // **Function to update form state**
    const handleFormChange = (field: keyof typeof formData, value: string | number) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    // **Reset function**
    const resetForm = () => {
        setFormData((prev) => ({
            ...prev,
            strategyTypeId: '',
            keyValue: '',
            aacsc: '',
            amazonAudienceName: '',
            category: '',
            subCategory: '',
            name: '',
            source: '',
            audienceFeeType: '',
        }));
    };

    // **Fetch dropdown options and update state**
    const fetchOptions = async () => {
        const response: AxiosResponse<AudienceMappingOptions> = await ApiService.getAudienceMappingOptions();
        if (response.status === 200) {
            setFormData((prev) => ({
                ...prev,
                options: response.data,
            }));
        }
    };

    const handleClose = () => {
        resetForm();
        setOpen(false);
    };

    function showInfoMessage(alertColor: AlertColor, message: string) {
        setInfoMessage({
            message: message,
            severity: alertColor,
            onClose: () => {
                setInfoMessage({ message: null });
            },
        });
    }

    const audienceMappingSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        try {
            const response = await ApiService.addAudienceMapping({
                strategyTypeId: formData.strategyTypeId,
                keyValue: formData.keyValue,
                aacsc: formData.aacsc,
                amazonAudienceName: formData.amazonAudienceName,
                category: formData.category,
                subCategory: formData.subCategory,
                name: formData.name,
                source: formData.source,
                audienceFeeType: formData.audienceFeeType,
            });

            if (response.status === 200) {
                const newItem = response.data;

                // Find the corresponding strategyType object
                const strategyTypeObj = formData.options?.strategyTypes.find((st) => st.id === newItem.strategyType.id);

                // Add full strategyType object to the item
                const enrichedItem = {
                    ...newItem,
                    strategyType: strategyTypeObj || null, // Ensure it's set correctly
                };

                // Update the list
                setList((prev) => (prev ? [enrichedItem, ...prev] : [enrichedItem]));

                showInfoMessage('success', 'Audience Mapping has been successfully created.');
            }
        } catch (error) {
            showInfoMessage('error', 'Failed to create Audience Mapping.');
        }

        handleClose();
    };

    const handleSwitchChange = (id: number) => (event: ChangeEvent<HTMLInputElement>) => {
        if (!list) return;
        const checked = event.target.checked;

        setList(
            list.map((row) => {
                if (row.id === id) {
                    const newRow = { ...row, disabled: checked };
                    ApiService.updateAudienceMapping(newRow);
                    return newRow;
                }
                return row;
            })
        );
    };

    const handleOpen = () => {
        resetForm();
        setOpen(true);
    };

    const editModalStyles = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        borderRadius: '8px',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
    };

    const fetchData = async () => {
        const response: AxiosPageableResponse<AudienceMapping> = await ApiService.getAudienceMappings(
            page,
            pageSize,
            sortModel
        );
        if (response.status === 200) {
            setList(response.data.content);
            setTotalElements(response.data.totalElements);
            setNumberOfElements(response.data.numberOfElements);
        }
    };

    useEffect(() => {
        fetchData();
        fetchOptions();
    }, [page, pageSize, sortModel]);

    const handleChange = (event: SelectChangeEvent<string>) => {
        const value = parseInt(event?.target?.value);
        if (pageValueArray.includes(value)) {
            setPageSize(value);
        } else {
            setPageSize(defaultPageSize);
        }
    };

    function onPageChange(model: GridPaginationModel) {
        setPage(model.page);
    }

    const columns: GridColDef[] = [
        {
            flex: 1,
            minWidth: 100,
            field: 'id',
            headerName: 'ID',
            display: 'flex',
        },
        {
            flex: 2,
            minWidth: 100,
            field: 'strategyType',
            headerName: 'Strategy Type',
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Select a Creative Strategy Type for the Audience.">
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        StrategyType
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
            editable: true,
            valueFormatter: (params: { value: { value: string } }) => {
                if (!params || !params.value) return '';
                return params.value.value || '';
            },
            renderEditCell: (params: GridRenderEditCellParams) => {
                // Get the current full object or null
                const currentValue = params.value?.id || null;

                return (
                    <Select
                        fullWidth
                        label="Strategy Type"
                        value={currentValue}
                        variant="outlined"
                        size="small"
                        required
                        onChange={(event) => {
                            const selectedId = event.target.value;
                            // Find the full object from your strategyTypes array
                            const selectedOption = formData.options?.strategyTypes?.find((st) => st.id === selectedId);

                            // Pass the entire object to the cell
                            params.api.setEditCellValue({
                                id: params.id,
                                field: params.field,
                                value: selectedOption, // Store the complete object
                            });
                        }}
                    >
                        {formData.options?.strategyTypes?.map((option) => (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        ))}
                    </Select>
                );
            },
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'keyValue',
            headerName: 'Key Value',
            editable: true,
            display: 'flex',
            renderHeader: () => (
                <Tooltip title="Double click the value to edit it" arrow>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: theme.palette.text.secondary,
                            fontWeight: theme.typography.fontWeightMedium,
                        }}
                    >
                        Key Value
                        <EditOutlinedIcon fontSize="small" style={{ marginLeft: 2 }} />
                    </div>
                </Tooltip>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'aacsc',
            headerName: 'Amazon Audience Category + Sub-Category',
            hideable: true,
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Amazon Audience Category + Sub-Category"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.amazonAudienceCategorySubCategory?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'amazonAudienceName',
            headerName: 'Amazon Audience Name',
            hideable: true,
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Amazon Audience Name"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.amazonAudienceNames?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'category',
            headerName: 'Category',
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Category"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.categories?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'subCategory',
            headerName: 'Sub-Category',
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Sub-Category"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.subCategories?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'name',
            headerName: 'Name',
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Name"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.audienceNames?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'source',
            headerName: 'Source',
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Source"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.sources?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 3,
            minWidth: 100,
            field: 'audienceFeeType',
            headerName: 'Audience Fee Type',
            hideable: true,
            editable: true,
            display: 'flex',
            renderEditCell: (params: GridRenderEditCellParams) => (
                <Select
                    fullWidth
                    label="Audience Fee Type"
                    value={params.value}
                    variant="outlined"
                    size="small"
                    required
                    onChange={(event) =>
                        params.api.setEditCellValue({
                            id: params.id,
                            field: params.field,
                            value: event.target.value,
                        })
                    }
                >
                    {formData.options?.audienceFeeTypes?.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            ),
        },
        {
            flex: 1,
            minWidth: 100,
            field: 'disabled',
            headerName: 'Disable',
            type: 'boolean',
            renderCell: (params) => {
                const value = params.value;
                return <Switch checked={value} onChange={handleSwitchChange(params.id as number)} />;
            },
        },
    ];

    return (
        <AccessGuard accessGroup={USER_GROUP_ADMIN}>
            <TabPanel index={tab.id} value={activeTab} sx={{ p: 0 }}>
                <InfoMessage {...infoMessage} />
                <Column gap={2}>
                    <Row justifyContent="space-between" alignItems="center">
                        <Row gap={0} sx={{ width: '100%' }}>
                            <Typography component="span" color="text.primary" variant="subtitle2" fontWeight={500}>
                                Instruction:{' '}
                                <Typography
                                    component="span"
                                    color="text.secondary"
                                    variant="subtitle2"
                                    fontWeight={500}
                                >
                                    {tab.description}
                                </Typography>
                            </Typography>
                        </Row>
                        <Row flex={0} justifyContent="flex-end">
                            <Button variant="text" onClick={handleOpen} sx={{ marginTop: '-1%' }}>
                                Add Audience
                            </Button>
                            <FormControl sx={{ minWidth: 120 }} size="small">
                                <InputLabel id="demo-select-small-label">Rows Per Page</InputLabel>
                                <Select
                                    labelId="demo-select-small-label"
                                    id="demo-select-small"
                                    value={pageSize.toString()}
                                    label="Rows Per Page"
                                    onChange={handleChange}
                                >
                                    {pageValueArray?.map((value) => {
                                        return (
                                            <MenuItem key={value} value={value}>
                                                {value}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Row>
                    </Row>
                    <Modal
                        open={open}
                        onClose={handleClose}
                        aria-labelledby="modal-modal-title"
                        aria-describedby="modal-modal-description"
                    >
                        <Box sx={editModalStyles}>
                            <Typography id="modal-modal-title" variant="h6" component="h2" sx={{ textAlign: 'center' }}>
                                Add Audience
                            </Typography>
                            <form onSubmit={audienceMappingSubmit} style={{ width: '100%' }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 2,
                                        mt: 2,
                                        width: '100%',
                                    }}
                                >
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Strategy Type</InputLabel>
                                        <Select
                                            value={formData.strategyTypeId}
                                            onChange={(e) => handleFormChange('strategyTypeId', e.target.value)}
                                            label="Strategy Type"
                                        >
                                            {formData.options?.strategyTypes?.map((option) => (
                                                <MenuItem key={option.value} value={option.id}>
                                                    {option.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        fullWidth
                                        label="Key Value"
                                        variant="outlined"
                                        size="small"
                                        required
                                        onChange={(e) => handleFormChange('keyValue', e.target.value)}
                                    />
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Amazon Audience Category + Sub-Category</InputLabel>
                                        <Select
                                            value={formData.aacsc}
                                            onChange={(e) => handleFormChange('aacsc', e.target.value)}
                                            label="Amazon Audience Category + Sub-Category"
                                        >
                                            {formData.options?.amazonAudienceCategorySubCategory?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Amazon Audience Name</InputLabel>
                                        <Select
                                            value={formData.amazonAudienceName}
                                            onChange={(e) => handleFormChange('amazonAudienceName', e.target.value)}
                                            label="Amazon Audience Name"
                                        >
                                            {formData.options?.amazonAudienceNames?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Category</InputLabel>
                                        <Select
                                            value={formData.category}
                                            onChange={(e) => handleFormChange('category', e.target.value)}
                                            label="Category"
                                        >
                                            {formData.options?.categories?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Sub-Category</InputLabel>
                                        <Select
                                            value={formData.subCategory}
                                            onChange={(e) => handleFormChange('subCategory', e.target.value)}
                                            label="Sub-Category"
                                        >
                                            {formData.options?.subCategories?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Name</InputLabel>
                                        <Select
                                            value={formData.name}
                                            onChange={(e) => handleFormChange('name', e.target.value)}
                                            label="Name"
                                        >
                                            {formData.options?.audienceNames?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Source</InputLabel>
                                        <Select
                                            value={formData.source}
                                            onChange={(e) => handleFormChange('source', e.target.value)}
                                            label="Source"
                                        >
                                            {formData.options?.sources?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth variant="outlined" size="small" required>
                                        <InputLabel>Audience Fee Type</InputLabel>
                                        <Select
                                            value={formData.audienceFeeType}
                                            onChange={(e) => handleFormChange('audienceFeeType', e.target.value)}
                                            label="Audience Fee Type"
                                        >
                                            {formData.options?.audienceFeeTypes?.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 4 }}>
                                    <Button
                                        variant="contained"
                                        sx={{ width: '120px' }}
                                        startIcon={<SaveIcon />}
                                        type="submit"
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{ width: '120px' }}
                                        onClick={handleClose}
                                        startIcon={<CloseIcon />}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </form>
                        </Box>
                    </Modal>
                    <CDGrid
                        id="audienceMapping"
                        variant="contained"
                        rowCount={totalElements}
                        initialState={{ pagination: { paginationModel: { page: page, pageSize: pageSize } } }}
                        columns={columns}
                        paginationMode={'server'}
                        sortingMode={'server'}
                        onSortModelChange={setSortModel}
                        onPaginationModelChange={onPageChange}
                        rows={list === null ? [] : list}
                        processRowUpdate={(newRow) => {
                            ApiService.updateAudienceMapping(newRow);
                            setList((prevRows) =>
                                prevRows === null
                                    ? []
                                    : prevRows.map((row) => {
                                          if (row.id === newRow.id) {
                                              return {
                                                  ...newRow,
                                                  strategyTypeId:
                                                      formData.options?.strategyTypes?.find(
                                                          (st) => st.id === newRow.strategyType.id
                                                      ) || newRow.strategyType.id,
                                              };
                                          } else {
                                              return row;
                                          }
                                      })
                            );
                            return newRow;
                        }}
                        onProcessRowUpdateError={() => {}}
                        slots={{
                            footer: () => (
                                <AudienceMappingFooterGrid
                                    totalElements={totalElements}
                                    numberOfElements={numberOfElements}
                                />
                            ),
                        }}
                    />
                </Column>
            </TabPanel>
        </AccessGuard>
    );
};

export default AudienceMappingTab;
